import { Tooltip, IconButton } from "@mui/material";
import { useStores } from "index";
import { observer } from "mobx-react-lite";
import { useRef, useState, useCallback } from "react";
import { CreateFolderDialog } from "../../Modals/FileManagerModal/Dialogs/CreateFolderDialog";
import { DeleteConfirmationDialog } from "../../Modals/FileManagerModal/Dialogs/DeleteConfirmationDialog";
import { MoveDialog } from "../../Modals/FileManagerModal/Dialogs/MoveDialog";
import { RenameDialog } from "../../Modals/FileManagerModal/Dialogs/RenameDialog";
import { ReactComponent as ArrowUpwardIcon } from 'images/icons/MediaLibrary/ArrowUpward.svg';
import { ReactComponent as DriveFileMoveIcon } from 'images/icons/MediaLibrary/DriveFileMove.svg';
import { ReactComponent as EditIcon } from 'images/icons/MediaLibrary/Edit.svg';
import { ReactComponent as DeleteIcon } from 'images/icons/MediaLibrary/Delete.svg';
import { ReactComponent as CreateNewFolderIcon } from 'images/icons/MediaLibrary/CreateNewFolder.svg';
import { ReactComponent as UploadFileIcon } from 'images/icons/MediaLibrary/UploadFile.svg';

export const ToolBar = observer(() => {
  const fileManagerStore = useStores().mediaLibraryStore;
  const { currentDirectoryId, allNodes, selectedIds } = fileManagerStore;
  const inputPic = useRef<HTMLInputElement>(null);

  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);

  const uploadNewImages = useCallback(() => {
    if (inputPic.current?.files?.length) {
      const files = Array.from(inputPic.current.files).map((file) => ({
        parentId: fileManagerStore.currentDirectoryId,
        data: file,
      }));

      fileManagerStore.uploadFiles(files);
    }
  }, [inputPic, fileManagerStore]);

  const handleGoUp = () => {
    const current = allNodes.find(n => n.id === currentDirectoryId);
    if (current?.parentId) {
      fileManagerStore.navigateTo(current.parentId);
    }
  };

  return (
    <>
      <div className="file-manager-toolbar__actions">
        <Tooltip title="Upload new file(s)">
          <IconButton
            onClick={() => inputPic.current?.click()}
          >
            <input
              type="file"
              id="file"
              accept="image/*"
              multiple={true}
              ref={inputPic}
              style={{ display: 'none' }}
              onChange={uploadNewImages}
            />
            <UploadFileIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Move selected">
          <span>
            <IconButton
              onClick={() => setMoveDialogOpen(true)}
              disabled={selectedIds.size === 0}
              sx={{ opacity: selectedIds.size === 0 ? 0.5 : 1 }}
            >
              <DriveFileMoveIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Rename selected">
          <span>
            <IconButton
              onClick={() => setRenameDialogOpen(true)}
              disabled={selectedIds.size !== 1}
              sx={{ opacity: selectedIds.size !== 1 ? 0.5 : 1 }}
            >
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Delete selected">
          <span>
            <IconButton
              onClick={() => setDeleteDialogOpen(true)}
              disabled={selectedIds.size === 0}
              sx={{ opacity: selectedIds.size === 0 ? 0.5 : 1 }}
            >
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Create folder">
          <IconButton onClick={() => setCreateFolderDialogOpen(true)}>
            <CreateNewFolderIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Go up one level">
          <IconButton onClick={handleGoUp} disabled={currentDirectoryId === '_root'} sx={{ opacity: currentDirectoryId === '_root' ? 0.5 : 1 }}>
            <ArrowUpwardIcon />
          </IconButton>
        </Tooltip>
      </div>

      <CreateFolderDialog open={createFolderDialogOpen} onClose={() => setCreateFolderDialogOpen(false)} />
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={async confirmed => {
          setDeleteDialogOpen(false);
          if (confirmed) await fileManagerStore.deleteSelected();
        }}
      />
      <MoveDialog open={moveDialogOpen} onClose={() => setMoveDialogOpen(false)} />
      <RenameDialog
        open={renameDialogOpen}
        currentName={
          allNodes.find(n => selectedIds.has(n.id))?.name ?? ''
        }
        onClose={async newName => {
          setRenameDialogOpen(false);
          if (!newName) return;
          const selectedId = Array.from(selectedIds)[0];
          await fileManagerStore.renameNode(selectedId, newName);
        }}
      />
    </>
  );
});